import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import API_URL from '../config/api';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || data?.detail || 'Password reset failed. Please try again.');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Password reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div className="glass rise-in" style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.logo}>ARMITA</h1>
          <p style={styles.tagline}>Set a new password</p>
        </div>

        {error && <div className="alert alert--danger" role="alert" style={styles.banner}>{error}</div>}

        <div style={styles.content}>
          {!token ? (
            <>
              <div className="alert alert--danger" role="alert" style={styles.banner}>
                This password reset link is invalid or incomplete.
              </div>
              <p style={styles.description}>
                Please request a new reset link to continue.
              </p>
              <div style={styles.linkRow}>
                <Link to="/forgot-password" style={styles.link}>Request a new link</Link>
              </div>
            </>
          ) : success ? (
            <>
              <div className="alert alert--success" role="alert" style={styles.banner}>
                Your password has been reset. You can now sign in with your new password.
              </div>
              <button
                type="button"
                className="btn btn--primary btn--block btn--lg"
                style={styles.submit}
                onClick={() => navigate('/login')}
              >
                Go to Sign In
              </button>
            </>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="field">
                <label className="field__label" htmlFor="new-password">New Password</label>
                <input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input"
                  placeholder="Min 8 characters"
                  autoComplete="new-password"
                  required
                  minLength={8}
                />
              </div>

              <div className="field">
                <label className="field__label" htmlFor="confirm-password">Confirm New Password</label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input"
                  placeholder="Re-enter new password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                />
              </div>

              <button
                type="submit"
                className="btn btn--primary btn--block btn--lg"
                style={styles.submit}
                disabled={loading}
              >
                {loading && <span className="spinner spinner--sm" aria-hidden="true" />}
                {loading ? 'Please wait...' : 'Reset Password'}
              </button>
            </form>
          )}

          <div style={styles.linkRow}>
            <Link to="/login" style={styles.link}>Back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg-canvas)',
    padding: 'var(--space-4)',
    boxSizing: 'border-box'
  },
  card: {
    padding: 'clamp(24px, 4vw, 36px)',
    width: '100%',
    maxWidth: '440px',
    maxHeight: 'min(92dvh, 92vh)',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box'
  },
  header: {
    textAlign: 'center',
    marginBottom: 'var(--space-5)',
    flexShrink: 0
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0
  },
  logo: {
    fontSize: '1.6rem',
    fontWeight: 700,
    color: 'var(--text-primary)',
    margin: 0,
    letterSpacing: '0.22em'
  },
  tagline: {
    fontSize: '0.88rem',
    color: 'var(--text-secondary)',
    margin: '6px 0 0 0',
    letterSpacing: '-0.01em'
  },
  banner: {
    marginBottom: 'var(--space-3)',
    flexShrink: 0
  },
  description: {
    fontSize: '0.88rem',
    color: 'var(--text-secondary)',
    lineHeight: 1.55,
    margin: '0 0 var(--space-2) 0',
    textAlign: 'center'
  },
  submit: {
    marginTop: 'var(--space-2)'
  },
  linkRow: {
    textAlign: 'center',
    marginTop: 'var(--space-4)'
  },
  link: {
    color: 'var(--accent)',
    fontSize: '0.85rem',
    textDecoration: 'none'
  }
};

export default ResetPasswordPage;
